import { Button } from '@/ui/actions/button';
import { Loader } from '@/ui/feedback/Loader';
import { Field } from '@/ui/inputs/Field';
import { toast } from '@/ui/overlays/toast';
import { darkTokens, lightTokens } from '@/ui/styles/token-values';
import {
  AddressElement,
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  type StripeAddressElementOptions,
  type StripeCardElementOptions,
  type StripeElementsOptions,
  loadStripe,
} from '@stripe/stripe-js';
import { PayAsYouGo } from 'Saas/billing/PayAsYouGo';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'App/ThemeContext';
import { useStore } from 'App/mstore';
import type Plan from 'App/mstore/types/plan';

import ENV from '../../../../env';

// Stripe renders in its own iframe, so it gets real colour strings, not CSS vars.
const cardOptions = (
  tokens: Record<string, string>,
): StripeCardElementOptions => ({
  style: {
    base: {
      color: tokens['content-primary'],
      iconColor: tokens['content-muted'],
      fontSmoothing: 'antialiased',
      fontSize: '14px',
      '::placeholder': { color: tokens['content-placeholder'] },
    },
    invalid: {
      color: tokens['content-danger'],
      iconColor: tokens['content-danger'],
    },
  },
  hidePostalCode: true,
});

const elementsOptions = (
  tokens: Record<string, string>,
  dark: boolean,
): StripeElementsOptions => ({
  appearance: {
    theme: dark ? 'night' : 'stripe',
    variables: {
      colorPrimary: tokens['action-accent-bg'],
      colorBackground: tokens['surface-default'],
      colorText: tokens['content-primary'],
      colorDanger: tokens['content-danger'],
      borderRadius: tokens['radius-control'],
    },
  },
  locale: 'auto',
});

/** `setup` starts pay as you go with a first card; `update` replaces the card on file. */
type Mode = 'setup' | 'update';

interface PaymentFormProps {
  plan: Plan;
  mode: Mode;
  onClose?: () => void;
  addressOptions: StripeAddressElementOptions;
  onAddressChange: (event: any) => void;
  isAddressComplete: boolean;
  tokens: Record<string, string>;
}

const PaymentForm = observer(
  ({
    plan,
    mode,
    onClose,
    addressOptions,
    onAddressChange,
    isAddressComplete,
    tokens,
  }: PaymentFormProps) => {
    const { t } = useTranslation();
    const { billingStore } = useStore();
    const stripe = useStripe();
    const elements = useElements();
    const [isCardComplete, setIsCardComplete] = useState(false);
    const isLoading = billingStore.updating || billingStore.loading;
    const setup = mode === 'setup';
    // a free plan can keep the card it has: Start plan only switches it on
    const hasCard = !!plan.billing.last4;

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (billingStore.updating) return;
      if (!stripe || !elements) return;

      let card: { brand: string; last4: string } | undefined;
      if (!hasCard || isCardComplete) {
        const cardElement = elements.getElement(CardElement);
        if (cardElement) {
          const { error, token } = await stripe.createToken(cardElement);
          if (error) {
            toast.error(error.message);
            return;
          }
          plan.billing.edit({ cardToken: token.id });
          card = token.card ?? undefined;
        }
      }

      const brand = card?.brand ?? plan.billing.brand;
      const last4 = card?.last4 ?? plan.billing.last4;
      try {
        if (setup && hasCard) {
          if (card) await billingStore.updatePlan(plan);
          // re-enables pay as you go and fetches the plan
          await billingStore.enablePlan();
        } else {
          await billingStore.updatePlan(plan);
          // the paid plan takes the meter and the banner's billing item away
          if (setup) billingStore.fetchPlan().catch(() => {});
        }
        onClose?.();
        toast.success(
          setup
            ? t('Billing set up. {{brand}} ending {{last4}} pays as you go.', {
                brand: brand ?? t('Your card'),
                last4,
              })
            : t('Billing details updated successfully'),
        );
      } catch (error) {
        toast.error(
          typeof error === 'string'
            ? error
            : t('An unexpected error occurred. Please try again.'),
        );
        console.error('Update plan error:', error);
      }
    };

    return (
      <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
        <Field
          label={t('Card details')}
          note={
            hasCard
              ? t('Current card on file ending in {{last4}}', {
                  last4: plan.billing.last4,
                })
              : t('Payments are processed by Stripe.')
          }
        >
          <CardElement
            onChange={(event) => setIsCardComplete(event.complete)}
            options={cardOptions(tokens)}
            className="rounded-control border border-border-default bg-surface-default px-4 py-3"
          />
        </Field>
        <Field label={t('Billing address')}>
          <AddressElement options={addressOptions} onChange={onAddressChange} />
        </Field>
        <div className="flex items-center justify-end gap-4">
          {onClose ? <Button onClick={onClose}>{t('Cancel')}</Button> : null}
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            disabled={!isAddressComplete || (!hasCard && !isCardComplete)}
          >
            {setup ? t('Start plan') : t('Update')}
          </Button>
        </div>
      </form>
    );
  },
);

/** Card and billing address form; lives in the right-hand drawer (`Components/ModalContext`).
 *  Open setup through `useSetUpBilling`, so every entry point shows the same drawer. */
function PaymentInformation({
  mode = 'update',
  onClose,
}: {
  mode?: Mode;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const { billingStore } = useStore();
  const { theme } = useTheme();
  const plan = billingStore.instance;
  const [stripePromise] = useState(() => loadStripe(ENV.STRIPE_KEY ?? ''));
  const [isAddressComplete, setIsAddressComplete] = useState(false);
  const dark = theme === 'dark';
  const tokens = dark ? darkTokens : lightTokens;
  const options = useMemo(() => elementsOptions(tokens, dark), [dark]);

  useEffect(() => {
    if (!billingStore.planFetched) void billingStore.fetchPlan();
  }, [billingStore.planFetched]);

  const handleAddressChange = useCallback(
    (event: any) => {
      setIsAddressComplete(event.complete);
      if (event.complete && event.value) {
        if (event.value.address) {
          const { line1, line2, postal_code, city, state, country } =
            event.value.address;
          plan.billing.address.edit({
            line1,
            line2,
            postal_code,
            city,
            state,
            country,
          });
        }
        if (event.value.name) {
          plan.billing.edit({ name: event.value.name });
        }
      }
    },
    [plan.billing.address, plan.billing.name],
  );

  const addressOptions: StripeAddressElementOptions = {
    mode: 'billing',
    defaultValues: {
      name: plan.billing.name || '',
      address: {
        line1: plan.billing.address.line1 || '',
        line2: plan.billing.address.line2 || '',
        postal_code: plan.billing.address.postal_code || '',
        city: plan.billing.address.city || '',
        state: plan.billing.address.state || '',
        country: plan.billing.address.country || '',
      },
    },
  };

  return (
    <Loader loading={billingStore.loading}>
      <div className="flex flex-col gap-6">
        {mode === 'setup' ? <PayAsYouGo /> : null}
        <Elements stripe={stripePromise} options={options}>
          <PaymentForm
            plan={plan}
            mode={mode}
            onClose={onClose}
            addressOptions={addressOptions}
            onAddressChange={handleAddressChange}
            isAddressComplete={isAddressComplete}
            tokens={tokens}
          />
        </Elements>
      </div>
    </Loader>
  );
}

export default observer(PaymentInformation);
